import { useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import BookmarkIcon from '@mui/icons-material/Bookmark';

import whiteLogoAsset, { ReactComponent as WhiteLogo } from "../../assets/logo/white-logo.svg";
import whiteLogoGlitchAsset from "../../assets/logo/white-logo-glitch.svg";
import categoryRunning from "../../assets/fitness-categories/corrida.webp";
import categoryStrength from "../../assets/fitness-categories/musculacao.webp";
import categoryNutrition from "../../assets/fitness-categories/nutricao.webp";
import categoryPilates from "../../assets/fitness-categories/pilates.webp";
import categoryYoga from "../../assets/fitness-categories/yoga.webp";
import systemLesson from "../../assets/mockups/system-lesson.png";
import webChat from "../../assets/mockups/web/Chat · Dark · Editable · 1280×720.png";
import webCheckout from "../../assets/mockups/web/Checkout · Dark · Editable · 1280×720.png";
import webCommunityGroup from "../../assets/mockups/web/Community Group · Dark · Editable · 1280×720.png";
import webCommunityHome from "../../assets/mockups/web/Community Home · Dark · Editable · 1280×720.png";
import webContent from "../../assets/mockups/web/Content · WellFit · Dark · Editable · 1280×720.png";
import webInsights from "../../assets/mockups/web/Insights · Dark · Editable · 1280×720.png";
import webSectionHome from "../../assets/mockups/web/Section Home · Dark · Editable · 1280×720.png";
import flagAustralia from "../../assets/flags/au.svg";
import flagCanada from "../../assets/flags/ca.svg";
import flagSpain from "../../assets/flags/es.svg";
import flagUnitedKingdom from "../../assets/flags/gb.svg";
import flagMexico from "../../assets/flags/mx.svg";
import flagPortugal from "../../assets/flags/pt.svg";
import flagUnitedStates from "../../assets/flags/us.svg";
import avatarOne from "../../assets/personas/avatars/avatar (1).png";
import avatarTwo from "../../assets/personas/avatars/avatar (2).png";
import avatarThree from "../../assets/personas/avatars/avatar (3).png";
import GlobalGlobe from "./components/GlobalGlobe";
import "./styles.css";

const APP_URL = "https://app.ginastee.com/";
const CONTACT_URL = "https://api.whatsapp.com/send/?phone=5511919829965";
const HELP_URL = "https://help.ginastee.com";
const manifestoAvatars = [avatarOne, avatarTwo, avatarThree];

const platformScreens = [
  {
    id: "community",
    label: "Comunidade",
    image: webCommunityHome,
    alt: "Página inicial de uma comunidade fitness personalizada no Ginastee",
  },
  {
    id: "groups",
    label: "Grupos",
    image: webCommunityGroup,
    alt: "Feed, ranking e planos de membros de um grupo no Ginastee",
  },
  {
    id: "content",
    label: "Conteúdos",
    image: webContent,
    alt: "Aula em vídeo e playlist de conteúdos dentro do Ginastee",
  },
  {
    id: "journeys",
    label: "Trilhas",
    image: webSectionHome,
    alt: "Trilha de treinamento com playlists e progresso no Ginastee",
  },
  {
    id: "chat",
    label: "Chat",
    image: webChat,
    alt: "Conversas diretas entre criadores e membros no Ginastee",
  },
  {
    id: "checkout",
    label: "Checkout",
    image: webCheckout,
    alt: "Checkout personalizado para assinatura de uma comunidade no Ginastee",
  },
  {
    id: "insights",
    label: "Insights",
    image: webInsights,
    alt: "Painel de insights com vendas, receita e novos membros no Ginastee",
  },
];

const screenSpecs = [
  {
    id: "01",
    step: "Conquiste",
    title: "Leve sua audiência para o seu espaço",
    description:
      "Um link na bio leva seguidores para uma experiência fitness altamente personalizável dentro do Ginastee.",
    type: "mobile",
    screenTitle: "Creator storefront",
    screenSubtitle: "Community home",
    dimensions: "1290 × 2796 px",
    request:
      "Perfil do creator, capa, prova social, programas ativos, plano de assinatura e CTA para entrar.",
  },
  {
    id: "02",
    step: "Empacote",
    title: "Transforme seu método em produto",
    description:
      "Organize treinos, aulas, programas e conteúdos em uma jornada premium com valor claro.",
    type: "web",
    screenTitle: "Program builder",
    screenSubtitle: "Content and workout editor",
    dimensions: "1600 × 1100 px",
    request:
      "Sidebar, estrutura do programa, módulos, editor de treino, estados de publicação e preview.",
  },
  {
    id: "03",
    step: "Engaje",
    title: "Faça membros voltarem todos os dias",
    description:
      "Desafios, rankings e comunidade transformam consumo passivo em participação real.",
    type: "mobile",
    screenTitle: "Member experience",
    screenSubtitle: "Community and challenge",
    dimensions: "1290 × 2796 px",
    request:
      "Feed da comunidade, progresso do desafio, sequência diária, ranking e próxima atividade.",
  },
  {
    id: "04",
    step: "Venda",
    title: "Receba e cresça globalmente",
    description:
      "Venda em mercados suportados pela Stripe e acompanhe receita, assinaturas e repasses.",
    type: "web",
    screenTitle: "Global revenue dashboard",
    screenSubtitle: "Sales, members and payouts",
    dimensions: "1600 × 1100 px",
    request:
      "MRR, vendas por país, membros ativos, conversão, pagamentos recentes e próximos repasses.",
  },
];

const features = [
  {
    number: "01",
    title: "Experiência personalizável",
    text: "Sua identidade, seus produtos e sua linguagem em um espaço reconhecível dentro do Ginastee.",
  },
  {
    number: "02",
    title: "Fitness first",
    text: "Treinos, aulas, programas, desafios e comunidade pensados para fitness e wellness.",
  },
  {
    number: "03",
    title: "Receita recorrente",
    text: "Assinaturas e produtos premium conectados à experiência do membro, sem mensalidade para começar.",
  },
  {
    number: "04",
    title: "Venda global",
    text: "Pagamentos seguros para levar seu método aos mercados e moedas suportados pela operação.",
  },
];

const faqs = [
  {
    question: "Preciso pagar para começar?",
    answer:
      "Não. Você pode estruturar seu espaço sem mensalidade, taxa de configuração ou custo de onboarding. A Ginastee ganha quando você vende.",
  },
  {
    question: "Vou ter um aplicativo com a minha marca?",
    answer:
      "Sua comunidade vive no aplicativo e na experiência web do Ginastee. Ela não é white-label, mas é altamente personalizável com sua identidade, produtos, conteúdos e linguagem.",
  },
  {
    question: "Posso vender para outros países e receber em dólar?",
    answer:
      "Sim. A estrutura de pagamentos usa Stripe e permite vender nos mercados e moedas suportados pela operação. Disponibilidade, conversão e repasse podem variar conforme o país e a conta conectada.",
  },
  {
    question: "O que posso vender no Ginastee?",
    answer:
      "Assinaturas, programas, treinos, aulas em vídeo, desafios e conteúdos premium para fitness e wellness.",
  },
  {
    question: "Como minha audiência entra na comunidade?",
    answer:
      "Você recebe um link para compartilhar nas redes sociais. A pessoa conhece sua oferta, assina e acessa sua comunidade pelo app ou pela web.",
  },
];

const manifesto =
  "Seguidores são apenas o começo. O Ginastee transforma atenção em receita recorrente sem fronteiras.";

const heroCategories = [
  {
    className: "tile-one",
    title: "Yoga",
    image: categoryYoga,
  },
  {
    className: "tile-two",
    title: "Pilates",
    image: categoryPilates,
  },
  {
    className: "tile-three",
    title: "Corrida",
    image: categoryRunning,
  },
  {
    className: "tile-four",
    title: "Musculação",
    image: categoryStrength,
  },
  {
    className: "tile-five",
    title: "Nutrição",
    image: categoryNutrition,
  },
];

const countryMarkets = [
  { flag: flagUnitedStates, name: "Estados Unidos" },
  { flag: flagPortugal, name: "Portugal" },
  { flag: flagUnitedKingdom, name: "Reino Unido" },
  { flag: flagMexico, name: "México" },
  { flag: flagCanada, name: "Canadá" },
  { flag: flagAustralia, name: "Austrália" },
  { flag: flagSpain, name: "Espanha" },
];

const audienceOptions = [10000, 25000, 50000, 100000, 250000, 500000];

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function formatCurrency(value, currency) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function useScrollExperience(setActiveJourney) {
  useEffect(() => {
    const main = document.querySelector("main.creator-home");
    const root = document.querySelector(".creator-home");
    const progressLabel = document.querySelector(".header-progress-value");
    const revealElements = document.querySelectorAll(".creator-home .reveal");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let previousJourney = 0;

    if (reduceMotion) {
      revealElements.forEach((element) => element.classList.add("is-visible"));
    }

    const observer = reduceMotion
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("is-visible");
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.15, rootMargin: "0px 0px -6%" },
        );

    if (observer)
      revealElements.forEach((element) => observer.observe(element));

    const update = () => {
      if (!main || !root) return;
      const viewport = main.clientHeight;
      const maxScroll = main.scrollHeight - viewport;
      const pageProgress = maxScroll > 0 ? main.scrollTop / maxScroll : 0;
      root.style.setProperty("--page-progress", pageProgress);
      if (progressLabel)
        progressLabel.textContent = `${Math.round(pageProgress * 100)}%`;

      const productSection = document.querySelector(".product-film-section");
      if (productSection) {
        const rect = productSection.getBoundingClientRect();
        const distance = Math.max(rect.height - viewport, 1);
        const productProgress = clamp(-rect.top / distance);
        root.style.setProperty("--product-progress", productProgress);
      }

      const manifestoSection = document.querySelector(".manifesto-section");
      if (manifestoSection) {
        const rect = manifestoSection.getBoundingClientRect();
        const manifestoProgress = clamp(
          (viewport * 0.78 - rect.top) / (rect.height * 0.56),
        );
        root.style.setProperty("--manifesto-progress", manifestoProgress);
      }

      const journeyTrack = document.querySelector(".journey-desktop");
      if (journeyTrack && window.innerWidth > 900) {
        const rect = journeyTrack.getBoundingClientRect();
        const distance = Math.max(rect.height - viewport, 1);
        const journeyProgress = clamp(-rect.top / distance);
        const rawJourneyPosition = journeyProgress * (screenSpecs.length - 1);
        const rawStep = Math.min(
          screenSpecs.length - 1,
          Math.floor(rawJourneyPosition),
        );
        const rawStepProgress = rawJourneyPosition - rawStep;
        const transitionProgress = clamp((rawStepProgress - 0.28) / 0.44);
        const easedTransition =
          transitionProgress *
          transitionProgress *
          (3 - 2 * transitionProgress);
        const journeyPosition = Math.min(
          screenSpecs.length - 1,
          rawStep + easedTransition,
        );
        const nextJourney = Math.min(
          screenSpecs.length - 1,
          Math.round(journeyPosition),
        );
        root.style.setProperty("--journey-progress", journeyProgress);

        const lowerStep = Math.floor(journeyPosition);
        const upperStep = Math.min(screenSpecs.length - 1, lowerStep + 1);
        const stepBlend = journeyPosition - lowerStep;
        const deviceProfile = (type) =>
          type === "mobile"
            ? { width: 292, height: 600, radius: 44 }
            : { width: 660, height: 420, radius: 20 };
        const fromDevice = deviceProfile(screenSpecs[lowerStep].type);
        const toDevice = deviceProfile(screenSpecs[upperStep].type);
        const interpolate = (from, to) => from + (to - from) * stepBlend;
        const journeyDevice = document.querySelector(".journey-device");

        if (journeyDevice) {
          journeyDevice.style.setProperty(
            "--device-width",
            `${interpolate(fromDevice.width, toDevice.width)}px`,
          );
          journeyDevice.style.setProperty(
            "--device-height",
            `${interpolate(fromDevice.height, toDevice.height)}px`,
          );
          journeyDevice.style.setProperty(
            "--device-radius",
            `${interpolate(fromDevice.radius, toDevice.radius)}px`,
          );
        }

        const animateJourneyLayers = (selector, distanceMultiplier) => {
          document.querySelectorAll(selector).forEach((element, index) => {
            const offset = index - journeyPosition;
            const opacity = clamp(1 - Math.abs(offset) * 1.35);
            element.style.opacity = opacity;
            element.style.transform =
              Math.abs(offset) < 0.001
                ? "none"
                : `translateY(${offset * distanceMultiplier}px)`;
            element.style.pointerEvents = opacity > 0.65 ? "auto" : "none";
          });
        };

        animateJourneyLayers("[data-journey-copy]", 44);
        animateJourneyLayers("[data-journey-screen]", 30);
        animateJourneyLayers("[data-journey-context]", 190);

        if (nextJourney !== previousJourney) {
          previousJourney = nextJourney;
          setActiveJourney(nextJourney);
        }
      }
    };

    main?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    update();

    return () => {
      observer?.disconnect();
      main?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [setActiveJourney]);
}

function ActionLink({ children, variant = "light", className = "", ...props }) {
  return (
    <a className={`action-link action-${variant} ${className}`} {...props}>
      <span>{children}</span>
      <ArrowOutwardRoundedIcon aria-hidden="true" />
    </a>
  );
}

function ScreenCanvas({ spec }) {
  const isMobile = spec.type === "mobile";
  return (
    <div className="screen-shell">
      <div className="screen-chrome">
        <span className="screen-brand">GINASTEE</span>
        <span className="screen-chrome-dots">
          <i />
          <i />
          <i />
        </span>
      </div>
      <div className="screen-wireframe">
        {!isMobile && (
          <aside className="wire-sidebar">
            <i />
            <i />
            <i />
            <i />
            <i />
          </aside>
        )}
        <div className="wire-content">
          <div className="wire-title">
            <i />
            <i />
          </div>
          <div className="wire-hero">
            <span>SCREEN TO PRODUCE</span>
          </div>
          <div className="wire-pills">
            <i />
            <i />
            <i />
          </div>
          <div className="wire-grid">
            <i />
            <i />
            <i />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScreenPlaceholder({ spec, compact = false }) {
  return (
    <div
      className={`screen-placeholder screen-placeholder-${spec.type} ${
        compact ? "is-compact" : ""
      }`}
    >
      <ScreenCanvas spec={spec} />
      <div className="screen-specification">
        <span>
          {spec.type === "mobile" ? "APP SCREEN" : "WEB SCREEN"} {spec.id}
        </span>
        <strong>{spec.screenTitle}</strong>
        <small>
          {spec.screenSubtitle} · {spec.dimensions}
        </small>
        {!compact && <p>{spec.request}</p>}
      </div>
    </div>
  );
}

function JourneyDevice({ activeJourney }) {
  return (
    <div className="journey-device" aria-live="polite">
      <div className="journey-device-frame">
        {screenSpecs.map((spec, index) => (
          <div
            className={`journey-device-slide screen-placeholder-${spec.type}`}
            data-journey-screen
            aria-hidden={index !== activeJourney}
            key={spec.id}
          >
            <ScreenCanvas spec={spec} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformShowcase() {
  const [activeScreen, setActiveScreen] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (
      isPaused ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }

    const advanceTimeout = window.setTimeout(() => {
      setActiveScreen((current) => (current + 1) % platformScreens.length);
    }, 3100);

    return () => window.clearTimeout(advanceTimeout);
  }, [activeScreen, isPaused]);

  const moveToScreen = (index, focusTab = false) => {
    const nextIndex = (index + platformScreens.length) % platformScreens.length;
    setActiveScreen(nextIndex);
    if (focusTab) tabRefs.current[nextIndex]?.focus();
  };

  const handleTabKeyDown = (event, index) => {
    const keyDirections = {
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
      Home: 0,
      End: platformScreens.length - 1,
    };

    if (keyDirections[event.key] === undefined) return;
    event.preventDefault();
    moveToScreen(keyDirections[event.key], true);
  };

  return (
    <section
      className={`platform-showcase ${isPaused ? "is-paused" : ""}`}
      id="produto"
      aria-labelledby="platform-showcase-title"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsPaused(false);
        }
      }}
    >
      <div className="platform-showcase-glow" aria-hidden="true" />
      <div className="platform-showcase-shell page-shell">
        <header className="platform-showcase-heading reveal">
          <span>Tudo o que você precisa. Um só lugar.</span>
          <h2 id="platform-showcase-title">
            O Ginastee reúne tudo que você precisa para escalar no nicho fitness.
          </h2>
          <p>
            Você não precisa de mais uma ferramenta. Precisa de uma plataforma
            para transformar seu método em um negócio.
          </p>
        </header>

        <div
          className="platform-showcase-tabs reveal"
          role="tablist"
          aria-label="Recursos da plataforma Ginastee"
        >
          {platformScreens.map((screen, index) => {
            const isActive = index === activeScreen;
            return (
              <button
                type="button"
                className={`platform-showcase-tab ${isActive ? "is-active" : ""}`}
                role="tab"
                id={`platform-tab-${screen.id}`}
                aria-controls={`platform-panel-${screen.id}`}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => moveToScreen(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                key={screen.id}
              >
                <span>{screen.label}</span>
                {isActive && (
                  <i
                    className="platform-showcase-tab-progress"
                    key={`${screen.id}-${activeScreen}`}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="platform-showcase-stage">
          <div className="platform-showcase-logo-art" aria-hidden="true">
            <img src={whiteLogoGlitchAsset} alt="" />
          </div>

          <div className="platform-showcase-frame">
            <div className="platform-showcase-viewport">
              {platformScreens.map((screen, index) => {
                const isActive = index === activeScreen;
                return (
                  <div
                    className={`platform-showcase-panel ${isActive ? "is-active" : ""}`}
                    role="tabpanel"
                    id={`platform-panel-${screen.id}`}
                    aria-labelledby={`platform-tab-${screen.id}`}
                    aria-hidden={!isActive}
                    key={screen.id}
                  >
                    <img
                      src={screen.image}
                      alt={screen.alt}
                      loading={index === 0 ? "eager" : "lazy"}
                      draggable="false"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeHeader() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const main = document.querySelector("main.creator-home");
    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnScroll = () => setOpen(false);

    document.addEventListener("pointerdown", closeOnOutsideClick);
    main?.addEventListener("scroll", closeOnScroll, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      main?.removeEventListener("scroll", closeOnScroll);
    };
  }, [open]);

  return (
    <header className="benchmark-header">
      <div className="header-gradient-blur" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="header-left">
        <a className="header-mark" href="#top" aria-label="Ginastee, início">
          <WhiteLogo className="header-logo" aria-hidden="true" />
        </a>
      </div>

      <div
        className={open ? "header-menu is-open" : "header-menu"}
        ref={menuRef}
      >
        <button
          type="button"
          className="header-progress"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
          <span>{open ? "Fechar" : "Menu"}</span>
          <b className="header-progress-value">0%</b>
        </button>

        <div className="header-menu-panel" aria-hidden={!open}>
          <span className="menu-group-label">Menu</span>
          <nav aria-label="Menu expandido">
            <a href="#produto" onClick={() => setOpen(false)}>
              Produto
            </a>
            <a href="#como-funciona" onClick={() => setOpen(false)}>
              Como funciona
            </a>
            <a href="#global" onClick={() => setOpen(false)}>
              Alcance global
            </a>
            <a href="#precos" onClick={() => setOpen(false)}>
              Preços
            </a>
          </nav>
          <div className="menu-divider" />
          <span className="menu-group-label">Outros</span>
          <div className="menu-secondary-links">
            <a href={HELP_URL}>Central de ajuda</a>
            <a href="/privacy-policy">Privacidade</a>
            <a href={CONTACT_URL}>Fale com a gente</a>
          </div>
          <div className="menu-divider" />
          <span className="menu-group-label">Social</span>
          <div className="menu-social-links">
            <a href="https://www.instagram.com/ginastee.app/">Instagram</a>
            <a href="https://www.youtube.com/@ginasteebr">YouTube</a>
            <a href="https://www.linkedin.com/company/ginastee/">LinkedIn</a>
          </div>
          <ActionLink className="header-menu-cta" href={APP_URL}>
            Começar
          </ActionLink>
        </div>
      </div>

      <div className="header-actions">
        <a href={APP_URL}>Entrar</a>
        <ActionLink href={APP_URL}>Começar</ActionLink>
      </div>
    </header>
  );
}

function RevenueCalculator() {
  const [audienceIndex, setAudienceIndex] = useState(2);
  const [conversion, setConversion] = useState(0.5);
  const [price, setPrice] = useState(39);
  const [currency, setCurrency] = useState("USD");

  const result = useMemo(() => {
    const members = Math.round(
      audienceOptions[audienceIndex] * (conversion / 100),
    );
    return { members, monthly: members * price };
  }, [audienceIndex, conversion, price]);

  return (
    <div className="calculator-panel">
      <div className="calculator-controls">
        <div className="calculator-control">
          <div>
            <label htmlFor="audience">Seguidores</label>
            <strong>
              {audienceOptions[audienceIndex].toLocaleString("pt-BR")}
            </strong>
          </div>
          <input
            id="audience"
            type="range"
            min="0"
            max={audienceOptions.length - 1}
            step="1"
            value={audienceIndex}
            onChange={(event) => setAudienceIndex(Number(event.target.value))}
          />
        </div>
        <div className="calculator-control">
          <div>
            <label htmlFor="conversion">Conversão</label>
            <strong>{conversion.toFixed(1).replace(".", ",")}%</strong>
          </div>
          <input
            id="conversion"
            type="range"
            min="0.1"
            max="2"
            step="0.1"
            value={conversion}
            onChange={(event) => setConversion(Number(event.target.value))}
          />
        </div>
        <div className="calculator-control">
          <div>
            <label htmlFor="price">Preço mensal</label>
            <span className="currency-toggle" role="group" aria-label="Moeda">
              {["USD", "BRL"].map((value) => (
                <button
                  type="button"
                  className={currency === value ? "is-active" : ""}
                  onClick={() => setCurrency(value)}
                  key={value}
                >
                  {value}
                </button>
              ))}
            </span>
          </div>
          <div className="calculator-price">
            <span>{currency === "USD" ? "US$" : "R$"}</span>
            <input
              id="price"
              type="number"
              min="1"
              value={price}
              onChange={(event) =>
                setPrice(Math.max(1, Number(event.target.value)))
              }
            />
          </div>
        </div>
      </div>
      <div className="calculator-output" aria-live="polite">
        <span>Potencial mensal</span>
        <strong>{formatCurrency(result.monthly, currency)}</strong>
        <p>{result.members} membros pagantes</p>
        <div>
          <span>Potencial em 12 meses</span>
          <b>{formatCurrency(result.monthly * 12, currency)}</b>
        </div>
      </div>
    </div>
  );
}

function HomeFooter() {
  return (
    <footer className="benchmark-footer">
      <div className="footer-brand">
        <img src={whiteLogoAsset} alt="Ginastee" />
        <p>Fitness creators, global communities, recurring revenue.</p>
      </div>
      <div className="footer-columns">
        <div>
          <strong>Produto</strong>
          <a href="#produto">Recursos</a>
          <a href="#como-funciona">Como funciona</a>
          <a href="#precos">Preços</a>
        </div>
        <div>
          <strong>Empresa</strong>
          <a href={HELP_URL}>Central de ajuda</a>
          <a href={CONTACT_URL}>+55 11 91982-9965</a>
          <a href="mailto:adm@ginastee.com">adm@ginastee.com</a>
        </div>
        <div>
          <strong>Social</strong>
          <a href="https://www.instagram.com/ginastee.app/">
            <InstagramIcon /> Instagram
          </a>
          <a href="https://www.youtube.com/@ginasteebr">
            <YouTubeIcon /> YouTube
          </a>
          <a href="https://www.linkedin.com/company/ginastee/">
            <LinkedInIcon /> LinkedIn
          </a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Ginastee</span>
        <span>Feito no Brasil para criadores sem fronteiras.</span>
      </div>
    </footer>
  );
}

export default function InfluencersHome() {
  const [activeJourney, setActiveJourney] = useState(0);
  useScrollExperience(setActiveJourney);

  return (
    <main className="creator-home" id="top">
      <HomeHeader />

      <section className="benchmark-hero">
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src="/media/home-yoga.mp4" type="video/mp4" />
        </video>
        <div className="hero-video-overlay" />
        {heroCategories.map((category) => (
          <figure className={`hero-category-card ${category.className}`} key={category.className}>
            <img src={category.image} alt={`${category.title}: categoria fitness`} />
            <span className="hero-category-favorite" aria-hidden="true">
              <BookmarkIcon />
            </span>
            <figcaption>{category.title}</figcaption>
          </figure>
        ))}

        <div className="hero-center reveal is-visible">
          <span>Fitness e wellness, do Brasil para o mundo</span>
          <h1>Transforme seguidores em receita recorrente global.</h1>
          <p>
            Crie uma comunidade que entrega seu método, mantém membros ativos e
            vende para o mundo inteiro.
          </p>
          <div className="hero-actions">
            <ActionLink href={APP_URL}>Começar a vender</ActionLink>
            <a href="#produto" className="text-link">
              Ver como funciona <ArrowForwardRoundedIcon />
            </a>
          </div>
        </div>
        <a href="#produto" className="hero-scroll">
          Scroll para explorar <span />
        </a>
      </section>

      <PlatformShowcase />

      <section className="product-film-section" id="produto-em-acao">
        <div className="product-film-sticky">
          <div className="product-film-frame">
            <div className="film-frame-header">
              <span>Ginastee product preview</span>
              <span>Community lesson</span>
            </div>
            <div className="product-lesson-preview">
              <img
                className="product-lesson-image"
                src={systemLesson}
                alt="Aula em vídeo dentro de uma comunidade Ginastee, com playlist e comentários"
              />
              <div className="screen-specification product-lesson-copy">
                <span>Conteúdos exclusivos</span>
                <strong>Seu método em um ambiente criado por você!</strong>
                <small>Vídeo, playlists e interação em um só lugar</small>
                <p>
                  Entregue aulas em vídeo com materiais de apoio, comentários,
                  avaliações e uma playlist que conduz o membro para o próximo
                  conteúdo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="manifesto-section">
        <p aria-label={manifesto}>
          {manifesto.split(" ").map((word, index) => (
            <span className="manifesto-token" key={`${word}-${index}`}>
              <span
                className="manifesto-word"
                style={{ "--word-index": index }}
                aria-hidden="true"
              >
                {word === "membros," ? "membros" : `${word} `}
              </span>
              {word === "membros," && (
                <>
                  <span
                    className="manifesto-avatars"
                    style={{ "--word-index": index }}
                    aria-hidden="true"
                  >
                    {manifestoAvatars.map((avatar, avatarIndex) => (
                      <img
                        src={avatar}
                        alt=""
                        key={avatar}
                        style={{ "--avatar-index": avatarIndex }}
                      />
                    ))}
                  </span>
                  <span
                    className="manifesto-word"
                    style={{ "--word-index": index }}
                    aria-hidden="true"
                  >
                    ,{" "}
                  </span>
                </>
              )}
            </span>
          ))}
        </p>
      </section>

      <section className="standard-section page-shell">
        <div className="section-intro reveal">
          <span className="micro-label">
            Construído para transformar influência em negócio
          </span>
          <h2>O feed chama atenção. O produto cria valor.</h2>
          <p>
            O Ginastee conecta tudo que acontece depois do post: entrega,
            comunidade, retenção e pagamento.
          </p>
        </div>
        <div className="feature-rows reveal">
          {features.map((feature) => (
            <article key={feature.number}>
              <span>{feature.number}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section  style={{display: "none"}} className="journey-section" id="como-funciona">
        <div className="journey-heading page-shell reveal">
          <span className="micro-label">Do seguidor à receita</span>
          <h2>Uma jornada. Quatro movimentos.</h2>
          <p>
            As telas abaixo são os assets que precisamos produzir para
            apresentar o produto com clareza e precisão.
          </p>
        </div>

        <div className="journey-desktop">
          <div className="journey-sticky page-shell">
            <div className="journey-copy-stack">
              {screenSpecs.map((spec) => (
                <div className="journey-copy" data-journey-copy key={spec.id}>
                  <span className="journey-index">
                    {spec.id}
                    <i />
                  </span>
                  <h3>{spec.title}</h3>
                  <p>{spec.description}</p>
                </div>
              ))}
            </div>
            <div className="journey-screen">
              <JourneyDevice activeJourney={activeJourney} />
              <div className="journey-bars">
                {screenSpecs.map((spec, index) => (
                  <i
                    className={index === activeJourney ? "is-active" : ""}
                    key={spec.id}
                  />
                ))}
              </div>
            </div>
            <div className="journey-context-stack">
              {screenSpecs.map((spec) => (
                <div
                  className="journey-context"
                  data-journey-context
                  key={spec.id}
                >
                  <span>{spec.step}</span>
                  <strong>{spec.screenTitle}</strong>
                  <p>{spec.request}</p>
                  <small>{spec.dimensions}</small>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="journey-mobile page-shell">
          {screenSpecs.map((spec) => (
            <article key={spec.id}>
              <span className="journey-index">
                {spec.id}
                <i />
              </span>
              <h3>{spec.title}</h3>
              <p>{spec.description}</p>
              <ScreenPlaceholder spec={spec} compact />
            </article>
          ))}
        </div>
      </section>

      <section className="global-section" id="global">
        <div className="global-layout">
          <div className="global-visual reveal">
            <GlobalGlobe />
          </div>
          <div className="global-content reveal">
            <span className="micro-label">Alcance global</span>
            <h2>Sua audiência já é global. Sua receita ainda não.</h2>
            <p>
              Venda em dólar para diversos países, apresente sua oferta em
              moedas internacionais e construa um negócio sem fronteiras.
            </p>
            <div className="country-market-row" aria-label="Países disponíveis">
              <div className="country-market-scroll">
                {countryMarkets.map((country) => (
                  <span className="country-pill" key={country.name}>
                    <img className="country-flag" src={country.flag} alt="" />
                    {country.name}
                  </span>
                ))}
              </div>
              <span className="country-count">+45 países</span>
            </div>
            <div className="global-stat">
              <span>Economia wellness global em 2024</span>
              <strong>US$ 6,8 tri</strong>
              <a
                href="https://globalwellnessinstitute.org/press-room/statistics-and-facts/"
                target="_blank"
                rel="noreferrer"
              >
                Global Wellness Institute <ArrowOutwardRoundedIcon />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="calculator-section page-shell">
        <div className="section-intro reveal">
          <span className="micro-label">Faça as contas</span>
          <h2>Aqui, suas redes sociais se tornam receita recorrente</h2>
          <p>
            Simule o potencial de faturamento estimado com base no tamanho
            da sua audiência
          </p>
        </div>
        <div className="reveal">
          <RevenueCalculator />
        </div>
        <small className="calculator-note">
          Simulação ilustrativa. Resultados dependem da oferta, conversão,
          retenção, taxas e condições de pagamento.
        </small>
      </section>

      <section className="pricing-section page-shell" id="precos">
        <div className="pricing-copy reveal">
          <span className="micro-label">Crescemos juntos</span>
          <h2>Lucre com a sua plataforma e só pague quando vender</h2>
          <p>Sem mensalidade, taxa de configuração ou custo de onboarding.</p>
        </div>
        <article className="pricing-card reveal">
          <div className="pricing-card-top">
            <span>Plano creator</span>
            <b>Sem mensalidade</b>
          </div>
          <p>Você paga por venda</p>
          <div className="pricing-rate">
            <strong>9,49%</strong>
            <span>por transação</span>
          </div>
          <div className="pricing-lines">
            <p>
              <span>Brasil</span>
              <b>+ R$ 1,99</b>
            </p>
            <p>
              <span>Exterior</span>
              <b>+ US$ 1,99 + Taxas Stripe</b>
            </p>
          </div>
          <ul>
            <li>
              <CheckRoundedIcon /> App e web
            </li>
            <li>
              <CheckRoundedIcon /> Conteúdo ilimitado
            </li>
            <li>
              <CheckRoundedIcon /> Pagamentos seguros
            </li>
          </ul>
          <ActionLink href={APP_URL}>Começar a monetizar agora</ActionLink>
          <small>
            Taxas de processamento, câmbio e disponibilidade variam conforme
            país, moeda e condições da Stripe.
          </small>
        </article>
      </section>

      <section className="faq-section page-shell" id="faq">
        <div className="faq-copy reveal">
          <h2>Perguntas diretas, respostas claras.</h2>
          <p>
            Quer conversar com uma pessoa?{" "}
            <a href={CONTACT_URL}>Fale com nosso time.</a>
          </p>
        </div>
        <div className="faq-list reveal">
          {faqs.map((faq, index) => (
            <details key={faq.question} open={index === 0}>
              <summary>
                <strong>{faq.question}</strong>
                <span>+</span>
              </summary>
              <p>{faq.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="closing-section">
        <div className="closing-copy reveal">
          <span className="micro-label">Seu próximo movimento</span>
          <h2>Sua audiência já está pronta para ir além do feed!!!</h2>
          <p>
            Transforme seguidores em membros, seu método em produto e sua
            influência em receita global.
          </p>
          <div>
            <ActionLink href={APP_URL}>Criar minha comunidade</ActionLink>
            <a href={CONTACT_URL} className="text-link">
              Conversar com um especialista <ArrowForwardRoundedIcon />
            </a>
          </div>
        </div>
      </section>

      <HomeFooter />
    </main>
  );
}
